import { deleteDoc, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useCallback, useEffect, useRef, useState } from 'react';
import { db } from '../../lib/firebase';
import {
  MAX_ATTEMPTS,
  countActions,
  deleteAction,
  getAction,
  listActions,
  subscribeActionOutboxChanges,
  updateAction,
  type ActionOutboxEntry,
} from '../../lib/actionOutbox';
import { FLUSH_SKIPPED, withFlushLock } from '../../lib/flushLock';

/**
 * 汎用アクション outbox の flush hook (docs/03 §7.5-3 層1、Phase 2)。
 *
 * `features/posts/useOutbox.ts` (投稿) / `features/insights/useEcwOutbox.ts` (ECW) と同型だが:
 *   - 対象は `lib/actionOutbox.ts` の **kind 駆動 generic store** (賛同・[後続] アンケート/検証/membership)。
 *   - flush 全体を **Web Locks (`lib/flushLock.ts`) でラップ**し、複数タブ / 複数インスタンスの
 *     無駄打ちを防ぐ (代表タブ 1 つだけが送る。冪等なので skip しても次回再 flush で取りこぼさない)。
 *   - online 時は **enqueue / 変化を検知して即 flush** し、賛同トグルの反映遅延を抑える
 *     (in-flight 中の重複起動は `flushingRef` で抑止)。
 *
 * 送信 (`sendActionEntry`) は kind ごとに分岐。agree は「決定的 doc path (= clientId) へ
 * setDoc(空) / deleteDoc」という generic な冪等送信。survey は `setDoc(merge:false)` 全置換 +
 * payload のタイムスタンプ方針 (serverTimestampFields / createOnlyTimestampFields) を flush 側で
 * generic 適用 (feature 固有 sender import 不要)。verification (submittedAt 温存) も同方式で後続追加予定。
 *
 * AppHome / MapPage に mount (副作用 hook)。現状 UI バッジは出さず、保留分の自動送信のみ担う。
 */

/** flush 直列化のロック名 (origin 内で 1 つ)。 */
const FLUSH_LOCK_NAME = 'citizen-walk-action-outbox-flush';

export interface ActionOutboxState {
  count: number;
  /** pending: status 未設定 or 'pending' な件数。 */
  pendingCount: number;
  /** failed: status='failed' (MAX_ATTEMPTS 超過) の件数。 */
  failedCount: number;
  items: ActionOutboxEntry[];
  isFlushing: boolean;
}

export interface ActionFlushOptions {
  /** true で status='failed' を除外 (auto flush 用)。省略 (手動) で failed も再試行。 */
  skipFailed?: boolean;
}

export interface ActionRetryResult {
  ok: boolean;
  notFound?: boolean;
  lastError?: string;
}

function classifyCounts(items: ActionOutboxEntry[]): { pendingCount: number; failedCount: number } {
  let pendingCount = 0;
  let failedCount = 0;
  for (const item of items) {
    if (item.status === 'failed') failedCount += 1;
    else pendingCount += 1;
  }
  return { pendingCount, failedCount };
}

/**
 * 1 件を Firestore へ送信 (kind ごとに分岐)。成功で resolve、失敗で throw (呼出側が attempts++)。
 *
 * agree: clientId が送信先 doc path そのもの。doc ID = uid 固定で本体は空 (存在=同感)。
 *   firestore.rules:164-168 は `create, delete: isOwner` のみ許可で **`update: if false`**。
 *   既存 doc へ `setDoc` を投げると rules では **update 評価 → 403** になるため、先に `getDoc` で
 *   存在を確認し「**目標状態と差があるときだけ**」書く:
 *     - op='set' かつ未存在 → setDoc (create)。既存なら no-op (= 既に同感、冪等成功)。
 *     - op='delete' かつ存在 → deleteDoc。未存在なら no-op (= 既に未同感)。
 *   これで update-deny を回避し、再送・coalesce で目標状態に到達済なら冗長 write / 冗長 trigger
 *   (onAgreeCreated/onAgreeDeleted) を発火させず agreeCount のズレも防ぐ。doc 本体が空なので
 *   「存在の有無」が状態を完全に表し、setDoc を skip しても情報欠落は無い。
 */
async function sendActionEntry(entry: ActionOutboxEntry): Promise<void> {
  switch (entry.kind) {
    case 'agree': {
      const ref = doc(db, entry.clientId);
      const snap = await getDoc(ref);
      if (entry.op === 'delete') {
        if (snap.exists()) await deleteDoc(ref);
      } else if (!snap.exists()) {
        await setDoc(ref, entry.payload ?? {});
      }
      return;
    }
    case 'survey':
    case 'verification': {
      // setDoc(merge:false) 全置換。payload の「タイムスタンプ方針」を generic に適用する
      // (serverTimestamp sentinel は flush 時に生成。createOnly 列は既存 doc があれば温存)。
      // survey=createdAt 温存 / verification=submittedAt 温存 を同一ロジックで扱う。
      const ref = doc(db, entry.clientId);
      const p = (entry.payload ?? {}) as {
        body?: Record<string, unknown>;
        serverTimestampFields?: string[];
        createOnlyTimestampFields?: string[];
      };
      const data: Record<string, unknown> = { ...(p.body ?? {}) };
      const createOnly = p.createOnlyTimestampFields ?? [];
      let existing: Record<string, unknown> | undefined;
      if (createOnly.length > 0) {
        // createdAt 温存のため既存 doc を 1 度だけ読む (再送で createdAt が巻き戻らないように)。
        const snap = await getDoc(ref);
        existing = snap.exists() ? (snap.data() as Record<string, unknown>) : undefined;
      }
      for (const f of p.serverTimestampFields ?? []) data[f] = serverTimestamp();
      for (const f of createOnly) {
        data[f] = existing && existing[f] !== undefined ? existing[f] : serverTimestamp();
      }
      await setDoc(ref, data, { merge: false });
      return;
    }
    default: {
      // 網羅性チェック (新 kind 追加時に compile error で気付く)。
      const never: never = entry.kind;
      throw new Error(`未対応の action kind: ${String(never)}`);
    }
  }
}

/**
 * 送信失敗時の attempts/status 更新 helper (posts/useOutbox.markItemFailedOnce 踏襲)。
 * clientId は必須なので id===undefined ガードは不要。
 */
async function markActionFailedOnce(entry: ActionOutboxEntry, error: unknown): Promise<string> {
  const lastError = error instanceof Error ? error.message : String(error);
  const nextAttempts = entry.attempts + 1;
  const nextStatus: ActionOutboxEntry['status'] =
    nextAttempts >= MAX_ATTEMPTS ? 'failed' : 'pending';
  await updateAction({ ...entry, lastError, attempts: nextAttempts, status: nextStatus });
  return lastError;
}

export function useActionOutbox(): {
  state: ActionOutboxState;
  flush: (
    options?: ActionFlushOptions,
  ) => Promise<{ sent: number; failed: number; skipped: number }>;
  retryOne: (clientId: string) => Promise<ActionRetryResult>;
  refresh: () => Promise<void>;
} {
  const [state, setState] = useState<ActionOutboxState>({
    count: 0,
    pendingCount: 0,
    failedCount: 0,
    items: [],
    isFlushing: false,
  });

  const isMountedRef = useRef(true);
  /** flush 実行中フラグ (変化検知 flush の再入抑止、state は closure stale のため ref)。 */
  const flushingRef = useRef(false);

  const refresh = useCallback(async () => {
    const items = await listActions();
    if (!isMountedRef.current) return;
    setState((s) => ({ ...s, items, count: items.length, ...classifyCounts(items) }));
  }, []);

  const flush = useCallback(async (options?: ActionFlushOptions) => {
    flushingRef.current = true;
    if (isMountedRef.current) setState((s) => ({ ...s, isFlushing: true }));
    let sent = 0;
    let failed = 0;
    let skipped = 0;
    try {
      const result = await withFlushLock(FLUSH_LOCK_NAME, async () => {
        const all = await listActions();
        // auto-flush (skipFailed) は failed を除外。手動は全件再試行。
        const items = options?.skipFailed
          ? all.filter((entry) => {
              if (entry.status === 'failed') {
                skipped += 1;
                return false;
              }
              return true;
            })
          : all;
        for (const entry of items) {
          try {
            await sendActionEntry(entry);
            await deleteAction(entry.clientId);
            sent += 1;
          } catch (e) {
            failed += 1;
            await markActionFailedOnce(entry, e);
          }
        }
        return true;
      });
      // result === FLUSH_SKIPPED: 他タブ / 他インスタンスが flush 中。送信は任せ、counts のみ finally で同期。
      void result;
    } finally {
      flushingRef.current = false;
      const remaining = await listActions();
      if (isMountedRef.current) {
        setState({
          count: remaining.length,
          items: remaining,
          isFlushing: false,
          ...classifyCounts(remaining),
        });
      }
    }
    return { sent, failed, skipped };
  }, []);

  const retryOne = useCallback(
    async (clientId: string): Promise<ActionRetryResult> => {
      const entry = await getAction(clientId);
      if (!entry) {
        await refresh();
        return { ok: false, notFound: true };
      }
      flushingRef.current = true;
      if (isMountedRef.current) setState((s) => ({ ...s, isFlushing: true }));
      try {
        const result = await withFlushLock(FLUSH_LOCK_NAME, async () => {
          await sendActionEntry(entry);
          await deleteAction(entry.clientId);
          return true;
        });
        if (result === FLUSH_SKIPPED) {
          // 他タブが flush 中。失敗扱いにはせず未送信のまま据え置く (次回 flush で送信)。
          await refresh();
          return { ok: false };
        }
        await refresh();
        return { ok: true };
      } catch (e) {
        const lastError = await markActionFailedOnce(entry, e);
        await refresh();
        return { ok: false, lastError };
      } finally {
        flushingRef.current = false;
        if (isMountedRef.current) setState((s) => ({ ...s, isFlushing: false }));
      }
    },
    [refresh],
  );

  // 初回 mount + online 復帰 + (online 時) 変化検知で自動 flush。
  useEffect(() => {
    isMountedRef.current = true;
    let cancelled = false;

    void (async () => {
      const fresh = await listActions();
      if (cancelled || !isMountedRef.current) return;
      setState({ count: fresh.length, items: fresh, isFlushing: false, ...classifyCounts(fresh) });
      if (fresh.length > 0 && navigator.onLine) {
        void flush({ skipFailed: true });
      }
    })();

    function onOnline() {
      void (async () => {
        const c = await countActions();
        if (c > 0) void flush({ skipFailed: true });
      })();
    }
    window.addEventListener('online', onOnline);

    // 変化検知: 他タブ / 同一タブの enqueue を refresh。online かつ flush 中でなければ即 flush し、
    // 賛同トグルの送信遅延を抑える (in-flight 中の enqueue は次回 trigger で送信される)。
    const unsubscribe = subscribeActionOutboxChanges(() => {
      void (async () => {
        await refresh();
        if (navigator.onLine && !flushingRef.current) {
          const c = await countActions();
          if (c > 0) void flush({ skipFailed: true });
        }
      })();
    });

    return () => {
      cancelled = true;
      isMountedRef.current = false;
      window.removeEventListener('online', onOnline);
      unsubscribe();
    };
  }, [flush, refresh]);

  return { state, flush, retryOne, refresh };
}
